import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { Check, X, Lightning, Star, Crown, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function PricingPage() {
  const { user, token, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const sessionId = searchParams.get("session_id");

  const checkPaymentStatus = useCallback(async (sid) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        toast.error("Payment verification timed out. Check your dashboard for status.");
        setCheckingPayment(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/subscription/status/${sid}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.payment_status === "paid") {
          toast.success("Payment successful! Your subscription is now active.");
          setCheckingPayment(false);
          navigate("/dashboard");
          return;
        } else if (response.data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          setCheckingPayment(false);
          return;
        }

        attempts++;
        setTimeout(poll, 2000);
      } catch (error) {
        console.error("Error checking payment:", error);
        attempts++;
        setTimeout(poll, 2000);
      }
    };

    poll();
  }, [token, navigate]);

  useEffect(() => {
    if (sessionId && user) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId, user, checkPaymentStatus]);

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.error("Please sign in to subscribe");
      return;
    }

    if (user.subscription === planId) {
      toast.info("You're already on this plan");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create checkout");
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    {
      id: "free",
      name: "Free",
      price: 0,
      period: "forever",
      description: "Get started with basic farming news",
      icon: Star,
      features: [
        { text: "Access to all news articles", included: true },
        { text: "5 saved articles", included: true },
        { text: "2 price alerts", included: true },
        { text: "Basic market data", included: true },
        { text: "Email notifications", included: false },
        { text: "SMS alerts", included: false },
        { text: "Ad-free experience", included: false },
        { text: "API access", included: false },
      ],
      cta: "Current Plan",
      popular: false
    },
    {
      id: "premium",
      name: "Premium",
      price: 9.99,
      period: "/month",
      description: "Perfect for active farmers and traders",
      icon: Lightning,
      features: [
        { text: "Everything in Free", included: true },
        { text: "Unlimited saved articles", included: true },
        { text: "Unlimited price alerts", included: true },
        { text: "Email notifications", included: true },
        { text: "Ad-free experience", included: true },
        { text: "Priority market data", included: true },
        { text: "SMS alerts", included: false },
        { text: "API access", included: false },
      ],
      cta: "Upgrade to Premium",
      popular: true
    },
    {
      id: "pro",
      name: "Professional",
      price: 29.99,
      period: "/month",
      description: "For serious agricultural professionals",
      icon: Crown,
      features: [
        { text: "Everything in Premium", included: true },
        { text: "SMS price alerts", included: true },
        { text: "API access", included: true },
        { text: "Weekly market reports", included: true },
        { text: "Priority support", included: true },
        { text: "Custom alert thresholds", included: true },
        { text: "Export data to CSV", included: true },
        { text: "Early access to features", included: true },
      ],
      cta: "Go Professional",
      popular: false
    }
  ];

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <main className="max-w-[600px] mx-auto px-4 py-20 text-center">
          <div className="animate-pulse mb-6">
            <div className="w-16 h-16 mx-auto bg-[#0F766E] rounded-full flex items-center justify-center">
              <Lightning size={32} className="text-white" />
            </div>
          </div>
          <h2 className="font-serif text-2xl text-[#33302E] mb-4">Verifying Payment...</h2>
          <p className="text-[#6B6661]">Please wait while we confirm your subscription.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEO title="Subscription Plans" description="Farmwatch subscription plans for premium market data, price alerts and agricultural intelligence. Free and premium options available." url="/pricing" />
      <Header />
      
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-serif text-4xl sm:text-5xl text-[#33302E] mb-4">
            Choose Your Plan
          </h1>
          <p className="font-sans text-lg text-[#6B6661] max-w-2xl mx-auto">
            Get the agricultural intelligence you need. Upgrade anytime for more features and real-time alerts.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => {
            const isCurrentPlan = user?.subscription === plan.id;
            const Icon = plan.icon;
            
            return (
              <div
                key={plan.id}
                className={`relative bg-white border ${
                  plan.popular ? "border-[#0F766E]" : "border-[#E0D4C8]"
                } p-8 flex flex-col`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#0F766E] text-white text-xs font-mono uppercase tracking-wider px-3 py-1">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className={`w-12 h-12 mx-auto mb-4 flex items-center justify-center ${
                    plan.popular ? "bg-[#0F766E]" : "bg-[#E5E7EB]"
                  }`}>
                    <Icon size={24} className={plan.popular ? "text-white" : "text-[#6B6661]"} />
                  </div>
                  <h3 className="font-serif text-2xl text-[#33302E]">{plan.name}</h3>
                  <p className="text-sm text-[#6B6661] mt-1">{plan.description}</p>
                </div>

                <div className="text-center mb-6">
                  <span className="font-mono text-4xl text-[#33302E]">
                    ${plan.price.toFixed(2)}
                  </span>
                  <span className="text-[#6B6661]">{plan.period}</span>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature) => (
                    <li key={`${plan.id}-${feature.text.slice(0, 20)}`} className="flex items-start gap-3">
                      {feature.included ? (
                        <Check size={18} className="text-[#059669] flex-shrink-0 mt-0.5" weight="bold" />
                      ) : (
                        <X size={18} className="text-[#E5E7EB] flex-shrink-0 mt-0.5" weight="bold" />
                      )}
                      <span className={`text-sm ${feature.included ? "text-[#33302E]" : "text-[#8B8580]"}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => plan.id !== "free" && handleSubscribe(plan.id)}
                  disabled={loading || isCurrentPlan || plan.id === "free"}
                  data-testid={`subscribe-${plan.id}`}
                  className={`w-full py-3 font-sans font-medium transition-colors ${
                    isCurrentPlan
                      ? "bg-[#E5E7EB] text-[#6B6661] cursor-default"
                      : plan.popular
                      ? "bg-[#0F766E] text-white hover:bg-[#0D9488]"
                      : plan.id === "free"
                      ? "bg-[#E5E7EB] text-[#6B6661] cursor-default"
                      : "bg-[#111827] text-white hover:bg-[#0F766E]"
                  } disabled:opacity-50`}
                >
                  {isCurrentPlan ? "Current Plan" : plan.cta}
                </button>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl text-[#33302E] text-center mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {[
              {
                q: "Can I cancel my subscription anytime?",
                a: "Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period."
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept all major credit and debit cards through Stripe's secure payment processing."
              },
              {
                q: "How do price alerts work?",
                a: "Set your target price for any commodity. When the market price crosses your threshold, you'll receive an instant notification via email (Premium) or SMS (Pro)."
              },
              {
                q: "Is there a free trial for Premium?",
                a: "We offer a 14-day money-back guarantee on all paid plans. If you're not satisfied, contact us for a full refund."
              }
            ].map((faq) => (
              <div key={faq.q.slice(0, 30)} className="border border-[#E0D4C8] bg-white p-6">
                <h3 className="font-sans font-medium text-[#33302E] mb-2">{faq.q}</h3>
                <p className="text-sm text-[#6B6661]">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
